import React, { Fragment, useEffect, useState, useRef, useMemo } from 'react'
import { shallowEqual, useSelector } from 'react-redux'
import { noop, getSourceTypeId } from 'utilities'
import { Drawer } from 'components/Shell/Shell'
import Portal from 'components/Portal/Portal'
import { Modal, ConfirmDialog } from 'components/Modal/Modal'
import RelinquishAccess from 'components/MyRecords/RelinquishAccess'
import AmendmentLauncher from 'components/MyRecords/AmendmentLauncher'
import ReinstatementLauncher from 'components/MyRecords/ReinstatementLauncher'
import { ConnectedRecordHistory } from 'components/RecordHistory/RecordHistory'
import ActionButton, { ActionButtonGroup } from 'components/ActionButton/ActionButton'
import DetailsList from 'components/DetailsList/DetailsList'
import UserGroupIcon from '@tecuity/sos-icons/react/user-group'
import FileBoxIcon from '@tecuity/sos-icons/react/file-box'
import HistoryIcon from '@tecuity/sos-icons/react/history'
import CertificateIcon from '@tecuity/sos-icons/react/certificate'
import DocumentEditIcon from '@tecuity/sos-icons/react/document-edit'
import DollarSignDoubleIcon from '@tecuity/sos-icons/react/dollar-sign-double'
import { LoginRedirectSwitch } from 'components/LoginRedirector/LoginRedirector'
import { useHistory } from 'react-router'
import RecordHistory from '../RecordHistory/RecordHistory'
import ManageRecordUserAccess from '../ManageRecordUserAccess/ManageRecordUserAccess'
import * as api from '../../requests'
import RequestRecordAccess from '../RequestRecordAccess/RequestRecordAccess'
import { SUBROUTES_OBJ } from '../../App'
import NewCollapsibleTable from '../Table/NewCollapsibleTable'

const ALLOW_CERTIFICATION_REQUEST =
  process.env.ALLOW_CERTIFICATION_REQUEST === undefined
    ? true
    : process.env.ALLOW_CERTIFICATION_REQUEST

const ALLOW_COPY_REQUEST =
  process.env.ALLOW_COPY_REQUEST === undefined ? true : process.env.ALLOW_COPY_REQUEST

const ALLOW_PUBLIC_CERTIFICATE_REQUEST = process.env.ALLOW_PUBLIC_CERTIFICATE_REQUEST
const RD_TOOLTIP_AMENDMENT = process.env.RD_TOOLTIP_AMENDMENT
const RD_TOOLTIP_REINSTATE = process.env.RD_TOOLTIP_REINSTATE
const RD_TOOLTIP_CERTIFICATE = process.env.RD_TOOLTIP_CERTIFICATE
const RD_TOOLTIP_HISTORY = process.env.RD_TOOLTIP_HISTORY
const RD_TOOLTIP_USER_ACCESS = process.env.RD_TOOLTIP_USER_ACCESS
const REQUIRE_ANNUAL_REPORT_CONFIRMATION = process.env.REQUIRE_ANNUAL_REPORT_CONFIRMATION
const AMENDMENTS_ALWAYS_ALLOWED_WHEN_LOGGED_IN =
  process.env.AMENDMENTS_ALWAYS_ALLOWED_WHEN_LOGGED_IN
const FILE_AMENDMENT = process.env.FILE_AMENDMENT
const VIEW_HISTORY = process.env.VIEW_HISTORY

const UpdatedRecordDrawer = (props) => {
  const loggedIn = useSelector((state) => state.auth.loggedIn, shallowEqual)
  const siteOffline = useSelector(
    (state) => state.app.appSettings.WEBSITE_PORTAL_OFFLINE_YN === true,
    shallowEqual
  )
  const location = useSelector((state) => state.router.location, shallowEqual)

  const history = useHistory()

  const [manageRecordUserAccessModalOpen, setManageRecordUserAccessModalOpen] = useState(false)
  const [relinquishAccessModalOpen, setRelinquishAccessModalOpen] = useState(false)
  const [requestRecordAccessModalOpen, setRequestRecordAccessModalOpen] = useState(false)
  const [amendmentLauncherOpen, setAmendmentLauncherOpen] = useState(false)
  const [reinstatementModalOpen, setReinstatementModalOpen] = useState(false)
  const [certificateModalOpen, setCertificateModalOpen] = useState(false)
  const [userOwnsCurrentRecord, setUserOwnsCurrentRecord] = useState(null)
  const [assumedNameHistoryOpen, setAssumedNameHistoryOpen] = useState(false)
  const [loadingDetails, setLoadingDetails] = useState(false)
  const [detailsError, setDetailsError] = useState(false)
  const [title, setTitle] = useState(props.title)
  const [subtitle, setSubtitle] = useState(props.subtitle)
  const [details, setDetails] = useState([])
  const [assumedNameHistory, setAssumedNameHistory] = useState({})
  const [arConfirmOpen, setArConfirmOpen] = useState(false)
  const [arConfirmFunction, setArConfirmFunction] = useState(null)
  const [specialAmendmentButtonLabel, setSpecialAmendmentButtonLabel] = useState(undefined)
  const [specialAmendmentFormDefinitionId, setSpecialAmendmentFormDefinitionId] = useState(undefined)
  const [hideCertButton, setHideCertButton] = useState(undefined)
  const [hideRequestAccess, setHideRequestAccess] = useState(undefined)
  const [hideManageAccess, setHideManageAccess] = useState(undefined)
  const [hideHistory, setHideHistory] = useState(undefined)
  const [hideAssumedNameHistory, setHideAssumedNameHistory] = useState(true)
  const [hideAmendmentButton, setHideAmendmentButton] = useState(undefined)
  const [hideSpecialAmendmentButton, setHideSpecialAmendmentButton] = useState(undefined)
  const [alwaysShowAmendmentButton, setAlwaysShowAmendmentButton] = useState(undefined)
  const [certButtonRedirectsTo, setCertButtonRedirectsTo] = useState(undefined)
  const [currentRecordSignal, setCurrentRecordSignal] = useState(null)
  const [assumedNameSignal, setAssumedNameSignal] = useState(null)
  const [orderFilingButtonLabel, setOrderFilingButtonLabel] = useState(undefined)
  const [showExpenditureButton, setShowExpenditureButton] = useState(false)
  const [expenditureModalData, setExpenditureData] = useState(undefined)
  const [expenditureModalOpen, setExpenditureModalOpen] = useState(false)

  const amendmentLauncherModal = useRef(null)
  const reinstatementModal = useRef(null)
  const certificateModal = useRef(null)
  const relinquishAccessModal = useRef(null)
  const assumendNameHistoryModal = useRef(null)
  const expenditureModal = useRef(null)

  const {
    show,
    onSidebarDismiss,
    category = '',
    sourceId,
    recordNum,
    canFileAR,
    canReinstate,
    canFileReinstatement,
    showCopyRequests,
    sublink,
    userOwnsCurrentRecord: userOwnsRecord,
    onRequestRecordsRefresh,
    selectedResult,
    uccRecordId = null,
  } = props

  const lobbyistRenewalLabel = ((SUBROUTES_OBJ && SUBROUTES_OBJ.lobbyist) || {})
    .myRecordsRenewalButtonLabel

  const miCheckRecordAccess = () => {
    if (category && sourceId)
      api.MI_CHECK_RECORD_ACCESS(
        sourceId,
        getSourceTypeId(category),
        (res) => {
          setUserOwnsCurrentRecord(res)
        },
        (err) => console.error('Error verifying ownership')
      )
  }

  const specialAmendmentOptions = () => {
    return {
      business: {
        label: specialAmendmentButtonLabel,
        tooltip:
          RD_TOOLTIP_AMENDMENT || RD_TOOLTIP_REINSTATE
            ? specialAmendmentButtonLabel
              ? specialAmendmentButtonLabel
              : canReinstate
              ? RD_TOOLTIP_REINSTATE
              : undefined
            : undefined,
        onClickFn: getWrappedAnualReportFunction(getBusinessSpecialAmendmentOnClickFn()),
      },
      charity: {
        label: specialAmendmentButtonLabel,
        tooltip: specialAmendmentButtonLabel,
        onClickFn: openSpecialAmendmentWizard
      },
      contractor: {
        label: specialAmendmentButtonLabel || 'File Contractor Renewal',
        onClickFn: getWrappedAnualReportFunction(openContractorRenewalWizard),
      },
      lobbyist: {
        label: specialAmendmentButtonLabel || lobbyistRenewalLabel || 'File Lobbyist Renewal',
        onClickFn: openSpecialAmendmentWizard,
      },
      trademark: {
        label: specialAmendmentButtonLabel || 'File Trademark Renewal',
        onClickFn: openSpecialAmendmentWizard,
      },
      notary: {
        label: specialAmendmentButtonLabel || 'File Renewal',
        onClickFn: openSpecialAmendmentWizard,
      },
    }
  }

  const getWrappedAnualReportFunction = (fn) => () => {
    if (REQUIRE_ANNUAL_REPORT_CONFIRMATION) {
      setArConfirmOpen(true)
      setArConfirmFunction(fn)
    } else {
      fn()
    }
  }
  const getBusinessSpecialAmendmentOnClickFn = () => {
    return openBusinessSpecialAmendmentWizard
  }

  const toggleReinstatementModal = () => {
    setReinstatementModalOpen((open) => !open)
  }
  const toggleAmendmentLauncher = () => {
    setAmendmentLauncherOpen((open) => !open)
  }
  const toggleRequestRelinquishAccessFunctionality = () => {
    miCheckRecordAccess()
    if (userOwnsCurrentRecord) {
      setRelinquishAccessModalOpen((open) => !open)
    } else {
      history.push('/requestaccess', {
        sourceId,
        sourceTypeId: category ? getSourceTypeId(category) : null,
        recordNum,
        category,
        registrationDate: selectedResult && selectedResult.RegistrationDate,
      })
    }
  }
  const toggleCertificateModalOpen = () => {
    setCertificateModalOpen((open) => !open)
  }
  const openSpecialAmendmentWizard = () => {
    history.push(`/amend/${specialAmendmentFormDefinitionId}/${recordNum}`)
  }
  const openBusinessSpecialAmendmentWizard = () => {
    api.GET_SPECIAL_AMENDMENT_FORM_DEFINITION_ID(
      recordNum,
      category,
      (formDefId) => {
        history.push(`/amend/${formDefId}/${recordNum}`)
      },
      console.error
    )
  }
  const openContractorRenewalWizard = () => {
    api.GET_CONTRACTOR_RENEWAL_FORM_DEFINITION_ID(
      recordNum,
      (formDefId) => {
        history.push(`/amend/${formDefId}/${recordNum}`)
      },
      console.error
    )
  }
  const redirectOrOpenCertificateModal = () => {
    if (sublink.certificateDownloadRedirectsTo) {
      history.push(sublink.certificateDownloadRedirectsTo.replace(/{{recordNum}}/g, recordNum))
    } else if (certButtonRedirectsTo) {
      history.push(certButtonRedirectsTo)
    } else {
      setCertificateModalOpen(true)
    }
  }

  const specialAmendmentCategory = specialAmendmentOptions()[category] || {}

  useEffect(() => {
    process.env.STATE_ABBR === 'MI' && miCheckRecordAccess()
    getRecordDetails()
  }, [sourceId, category, recordNum])

  const getRecordDetails = () => {
    setDetailsError(false)
    if (category && sourceId) {
      if (currentRecordSignal && currentRecordSignal.Abort) {
        currentRecordSignal.Abort()
      }
      if (assumedNameSignal && assumedNameSignal.Abort) {
        assumedNameSignal.Abort()
      }
      setLoadingDetails(true)

      api.GET_FILING_DETAILS(
        category,
        sourceId,
        (signal) => {
          setCurrentRecordSignal(() => signal)
        },
        (data) => {
          setLoadingDetails(false)
          setDetailsError(false)
          setDetails(data.DRAWER_DETAIL_LIST)
          setSpecialAmendmentButtonLabel(data.SPECIAL_AMENDMENT_BUTTON_LABEL)
          setHideCertButton(data.HIDE_CERT_BUTTON)
          setHideRequestAccess(data.HIDE_REQUEST_ACCESS)
          setHideManageAccess(data.HIDE_MANAGE_ACCESS)
          setHideHistory(data.HIDE_HISTORY)
          setHideAmendmentButton(data.HIDE_AMENDMENT_BUTTON)
          setHideSpecialAmendmentButton(data.HIDE_SPECIAL_AMENDMENT_BUTTON)
          setAlwaysShowAmendmentButton(data.ALWAYS_SHOW_AMENDMENT_BUTTON)
          setCertButtonRedirectsTo(data.CERT_BUTTON_REDIRECTS_TO)
          setSpecialAmendmentFormDefinitionId(data.SpecialAmendmentFormDefinitionId)
          setOrderFilingButtonLabel(data.OrderFilingButtonLabel)
        },
        (err) => {
          setLoadingDetails(false)
          setDetails([])
          // An AbortError just means a newer request superseded this one
          // (e.g. the user selected another record) — that is not a failure,
          // so don't surface the error state for it.
          if (err.name !== 'AbortError') {
            console.error(err)
            setDetailsError(true)
          }
        }
      )

      if (category === 'business') {
        api.GET_ASSUMED_NAME_HISTORY(
          sourceId,
          (signal) => {
            setAssumedNameSignal(() => signal)
          },
          (data) => {
            setAssumedNameHistory(data || {})
            setHideAssumedNameHistory(
              !data || !data.IsEnabled || data.Names.length === 0 ? true : false
            )
          },
          (err) => {
            console.error(err)
          }
        )
        setHideAssumedNameHistory(false)
      }

      if (category === 'lobbyist') {
        api.GET_LOBBYIST_EXPENDITURES(
          sourceId,
          (data) => {
            setExpenditureData(data || {})
            setShowExpenditureButton(true)
          },
          (err) => {
            setShowExpenditureButton(false)
            console.error(err)
          }
        )
      }
      else {
        setShowExpenditureButton(false)
        setExpenditureData({})
      }
    }
  }

  useEffect(() => {
    if (category !== 'business') setHideAssumedNameHistory(true)
  }, [category])

  useEffect(() => {
    setTitle(props.title)
  }, [props.title])

  useEffect(() => {
    setSubtitle(props.subtitle)
  }, [props.subtitle])

  const sourceTypeId = useMemo(() => {
    return category ? getSourceTypeId(category) : null
  }, [category])

  return (
    <Fragment>
      <Drawer
        show={!!show}
        title={title}
        subtitle={subtitle}
        onSidebarDismiss={onSidebarDismiss}
        showCloseButton
        alwaysShowClosebutton
      >
        <div
          className={`${
            (loadingDetails && !userOwnsRecord) || loadingDetails ? 'record-loading' : ''
          }`}
        >
          {detailsError ? (
            <p
              className="error-message record-drawer-error"
              role="alert"
              aria-live="polite"
              style={{ padding: '15px', textAlign: 'center', fontSize: '.95rem' }}
            >
              We were unable to load the details for this record. Please close this panel and try
              again.
            </p>
          ) : (
          <Fragment>
          <ActionButtonGroup className="details-actions-top">
            {(alwaysShowAmendmentButton ||
              (AMENDMENTS_ALWAYS_ALLOWED_WHEN_LOGGED_IN && loggedIn) ||
              !hideAmendmentButton) && (
              <ActionButton
                label={sublink.fileAmendmentButtonLabel || FILE_AMENDMENT || 'File Amendment'}
                children={<DocumentEditIcon />}
                onClick={toggleAmendmentLauncher}
                tooltip={RD_TOOLTIP_AMENDMENT}
              />
            )}
            {!hideSpecialAmendmentButton && (
              <ActionButton
                label={specialAmendmentCategory.label}
                iconClass="icon icon-report"
                onClick={specialAmendmentCategory.onClickFn}
                tooltip={specialAmendmentCategory.tooltip}
              />
            )}
            {sublink.showRequestCertificate !== false &&
              (ALLOW_CERTIFICATION_REQUEST !== false || ALLOW_PUBLIC_CERTIFICATE_REQUEST) &&
              hideCertButton !== true &&
              !siteOffline && (
                <ActionButton
                  label={
                    sublink.requestCertificateAlternateLabel ||
                    orderFilingButtonLabel ||
                    'Request Certificate'
                  }
                  children={<CertificateIcon />}
                  onClick={redirectOrOpenCertificateModal}
                  tooltip={RD_TOOLTIP_CERTIFICATE}
                />
              )}
          </ActionButtonGroup>
          {(details || []).length > 0 && <DetailsList details={details} />}
          <ActionButtonGroup className="details-actions-bottom">
            {hideHistory ? null : (
              <ConnectedRecordHistory
                category={category}
                amendmentNum={recordNum}
                userOwnsRecord={userOwnsRecord}
              >
                {({ openModal, loading }) => (
                  <ActionButton
                    label={VIEW_HISTORY || 'View History'}
                    onClick={openModal}
                    loading={loading}
                    disabled={loadingDetails && !userOwnsRecord}
                    children={<HistoryIcon />}
                    tooltip={RD_TOOLTIP_HISTORY}
                  />
                )}
              </ConnectedRecordHistory>
            )}
            {loggedIn && process.env.STATE_ABBR == 'MI' && (
              <ActionButton
                label={userOwnsCurrentRecord ? 'Remove From My Records' : 'File Online'}
                onClick={toggleRequestRelinquishAccessFunctionality}
                disabled={loadingDetails}
                children={<FileBoxIcon />}
                tooltip={RD_TOOLTIP_USER_ACCESS}
              />
            )}
            {hideAssumedNameHistory ? null : (
              <ActionButton
                label="Assumed Names"
                onClick={() => setAssumedNameHistoryOpen(true)}
                disabled={loadingDetails && !userOwnsRecord}
                children={<HistoryIcon />}
                tooltip={RD_TOOLTIP_USER_ACCESS}
              />
            )}
            {showExpenditureButton ? (
              <ActionButton
                label="View Expenditures"
                onClick={() => setExpenditureModalOpen(true)}
                disabled={loadingDetails}
                children={<DollarSignDoubleIcon/>}
                tooltip={'Click to view the expenditures for this Lobbyist Record.'}
              />
            ) : null}
            {!siteOffline &&
              sublink.HideRecordUserManagment !== true &&
              loggedIn &&
              !hideManageAccess && (
                <ActionButton
                  label="Manage User Access"
                  onClick={() => {
                    setManageRecordUserAccessModalOpen(true)
                  }}
                  disabled={loadingDetails}
                  children={<UserGroupIcon />}
                  tooltip={RD_TOOLTIP_USER_ACCESS}
                />
              )}
            {!siteOffline &&
              sublink.HideRecordUserManagment !== true &&
              loggedIn &&
              !hideRequestAccess && (
                <ActionButton
                  label="Request Access"
                  onClick={() => {
                    setRequestRecordAccessModalOpen(true)
                  }}
                  disabled={loadingDetails}
                  children={<UserGroupIcon />}
                  tooltip={RD_TOOLTIP_USER_ACCESS}
                />
              )}
            {showCopyRequests && ALLOW_COPY_REQUEST && !siteOffline && (
              <ActionButton label="Copy Requests" iconClass="icon icon-download" onClick={null} />
            )}
          </ActionButtonGroup>
          </Fragment>
          )}
        </div>
      </Drawer>
      <Portal isOpened={relinquishAccessModalOpen}>
        <Modal
          title="Relinquish Access"
          closeModal={toggleRequestRelinquishAccessFunctionality}
          modalSize="short"
          hideHeader
          ref={relinquishAccessModal}
          closeButton
        >
          <RelinquishAccess
            sourceId={sourceId}
            sourceNum={recordNum}
            sourceTypeId={category ? getSourceTypeId(category) : null}
            onRelinquishModalClose={toggleRequestRelinquishAccessFunctionality} 
          />
        </Modal>
      </Portal>
      <Portal isOpened={manageRecordUserAccessModalOpen}>
        <Modal
          title={`Manage User Access - ${title}`}
          closeModal={() => setManageRecordUserAccessModalOpen(false)}
          modalSize="extra-large"
          closeButton
          autoHeight
        >
          <ManageRecordUserAccess
            sourceId={sourceId}
            sourceTypeId={sourceTypeId === 462 ? 157 : sourceTypeId}
            onRequestRecordsRefresh={onRequestRecordsRefresh}
            uccRecordId={uccRecordId}
          />
        </Modal>
      </Portal>
      <Portal isOpened={requestRecordAccessModalOpen}>
        <Modal
          title={`Request Access - ${title}`}
          closeModal={() => {
            setRequestRecordAccessModalOpen(false)
          }}
          modalSize="medium"
          autoHeight
          closeButton
        >
          {loggedIn ? (
            <RequestRecordAccess
              sourceId={sourceId}
              sourceTypeId={sourceTypeId === 462 ? 157 : sourceTypeId}
              uccRecordId={uccRecordId}
            />
          ) : (
            <LoginRedirectSwitch location={location} />
          )}
        </Modal>
      </Portal>
      <Portal isOpened={amendmentLauncherOpen}>
        <Modal
          title={sublink.fileAmendmentButtonLabel || FILE_AMENDMENT || 'File Amendment'}
          closeModal={toggleAmendmentLauncher}
          ref={amendmentLauncherModal}
          modalSize="small"
          closeButton
        >
          <AmendmentLauncher filingNum={recordNum} category={category} />
        </Modal>
      </Portal>
      <Portal isOpened={reinstatementModalOpen}>
        <Modal
          title="Reinstate"
          closeModal={toggleReinstatementModal}
          ref={reinstatementModal}
          modalSize="small"
          closeButton
          autoHeight
        >
          <ReinstatementLauncher
            filingNum={recordNum}
            category={category}
            canFileAR={canFileAR}
            canFileReinstatement={canFileReinstatement}
          />
        </Modal>
      </Portal>
      <Portal isOpened={certificateModalOpen}>
        <Modal
          title={sublink.requestCertificateAlternateLabel || 'Request Certificate'}
          closeModal={toggleCertificateModalOpen}
          ref={certificateModal}
          modalSize="small"
          closeButton
        >
          <AmendmentLauncher
            filingNum={recordNum}
            category={category}
            isOrderFiling={true}
            sourceId={sourceId}
          />
        </Modal>
      </Portal>
      <Portal isOpened={arConfirmOpen}>
        <ConfirmDialog
          closeModal={() => setArConfirmOpen(false)}
          sizeOverride="toast toast-wide"
          leftAligned
          onModalClosed={noop}
          message={
            <span>
              You are about to complete an online form for the record below.  Review the information
              to ensure this is the correct record:
              <br />
              <br />
              <strong>Name</strong>: {title} <br />
              <strong>SOS Control ID#</strong>: {recordNum}
              <br />
              <strong>Record Type</strong>: {category === 'business' ? subtitle : sublink.label}
              <br />
              <br />
              Is this the correct record for which you wish to complete the online form?
            </span>
          }
          buttons={[
            {
              label: 'Yes',
              onClick: arConfirmFunction,
              className: 'btn-primary',
            },
            { label: 'No', onClick: noop, className: 'btn-default' },
          ]}
          modalStyle={{ borderRadius: 3 }}
        />
      </Portal>
      <Portal isOpened={assumedNameHistoryOpen}>
        <Modal
          title={'Assumed Names'}
          closeModal={() => setAssumedNameHistoryOpen(false)}
          ref={assumendNameHistoryModal}
          modalSize="medium"
          closeButton
          autoHeight
        >
          <div style={{ maxHeight: '85vh', overflowY: 'scroll' }}>
            <RecordHistory
              history={{
                amendments: assumedNameHistory.Names || [],
                history: [], // this would be for use with changed from / changed to
                template: assumedNameHistory.Template || [],
              }}
            />
          </div>
        </Modal>
      </Portal>
      <Portal isOpened={expenditureModalOpen}>
        <Modal
          title={`Expenditures by Legislative Year`}
          closeModal={() => setExpenditureModalOpen(false)}
          ref={expenditureModal}
          modalSize="large"
          closeButton
          autoHeight
        >
          {expenditureModalData && (expenditureModalData.YearlyExpenditures || []).length > 0 ? (
            <div style={{ maxHeight: '85vh', overflowY: 'scroll', padding: '10px'}}>
              <NewCollapsibleTable
                rows={expenditureModalData.YearlyExpenditures}
                mainTemplate={expenditureModalData.MainTemplate}
                subTemplates={expenditureModalData.SubTemplates}
                expandable
                hideMainHeader={true}
                hideSubTableLabels={true}
                className={'expenditures'}
                showCollapseAllButton={true}
              />
            </div>
          ) : (
            <h3 style={{ display: 'flex', justifySelf: 'center', fontSize: '21px', padding: '5px' }}>
              Selected Lobbyist has no Expenditures
            </h3>
          )}
        </Modal>
      </Portal>
    </Fragment>
  )
}

export default UpdatedRecordDrawer
